import React from 'react';
import bannerBuildings from '../assets/banner-buildings.png';
import { BannerStep } from '../types';

interface Props {
  title: string;
  /** Exactly three steps in this order:
   *    [0] Satış tarixi
   *    [1] Gözləmə qaydası tarixi
   *    [2] Satışın bitmə tarixi
   *
   *  Matches Figma file `Jc6u8Jnc3cpXz4UMAPFSHx`, page "Cover", frame "1".
   *  The implicit timeline start is `published_on` (no card for it). */
  steps: BannerStep[];
  /** True while the announcement is still being fetched — the banner
   *  hides its "—" placeholders behind a shimmering skeleton so the
   *  first paint isn't a distracting flash of dashes. */
  loading?: boolean;
}

/* Static hourly tick labels rendered below the dashed track, between
 * the step-2 and step-3 dots. Based on the Figma "Frame 1984078543"
 * element in the "1" banner of Done 14, re-scoped to a 13:00 → 15:00
 * range: three hourly labels spread across the same span, with a
 * symmetric opacity fade (full → faded → full). They are part of the
 * *design*, not API data — the API-driven times live on the three date
 * cards. Positions and the fade live in `.sa-banner__tick--N`. */
const HOUR_TICKS = ['13:00', '14:00', '15:00'];

/* Pad a "H:mm" / "HH:mm" time so the hour is always two digits
 * (e.g. "0:00" → "00:00"). */
const padTime = (time: string): string => {
  const [hour, ...rest] = time.split(':');
  return [hour.padStart(2, '0'), ...rest].join(':');
};

const AnnouncementBanner: React.FC<Props> = ({ title, steps, loading }) => (
  <section
    className={
      'sa-banner sa-banner--cover1' +
      (loading ? ' sa-banner--loading' : '')
    }
  >
    {/* ---------- decorative background ---------- */}
    <div className="sa-banner__bg">
      <img src={bannerBuildings} alt="" className="sa-banner__bg-image" />
      <div className="sa-banner__bg-overlay" />
      <div className="sa-banner__bg-glow" />
    </div>

    {/* ---------- title ---------- */}
    <h1 className="sa-banner__title">{title}</h1>

    {/* ---------- timeline ---------- */}
    <div className="sa-banner__timeline">
      {/* Step labels (frosted pills) — each at its own staircase height. */}
      {steps.map((step, i) => (
        <div
          key={`label-${i}`}
          className={`sa-banner__label sa-banner__label--${i + 1}`}
        >
          {step.label}
        </div>
      ))}

      {/* Dashed connectors from each label down to its dot. */}
      {steps.map((_, i) => (
        <span
          key={`conn-${i}`}
          className={`sa-banner__connector sa-banner__connector--${i + 1}`}
        />
      ))}

      {/* Solid blue drop-lines from each dot to its date card. */}
      {steps.map((_, i) => (
        <span
          key={`drop-${i}`}
          className={`sa-banner__droppline sa-banner__droppline--${i + 1}`}
        />
      ))}

      {/* Main dashed horizontal track + glowing progress section. */}
      <div className="sa-banner__track">
        <div className="sa-banner__track-progress" />
      </div>
      <span className="sa-banner__track-end" />

      {/* Hour-tick scale (Frame 1984078543 in Figma). Diamond + label
          per hour, five ticks 12:00 → 16:00 in slots `--1`..`--5`.
          The per-tick opacity fade lives in the SCSS (full / 0.4 /
          0.1 / 0.4 / full). */}
      <div className="sa-banner__ticks">
        {HOUR_TICKS.map((label, i) => (
          <div
            key={label}
            className={`sa-banner__tick sa-banner__tick--${i + 1}`}
          >
            <span className="sa-banner__tick-label">{label}</span>
            <span className="sa-banner__tick-mark" aria-hidden />
          </div>
        ))}
      </div>

      {/* Dots — three step indicators sitting on the main track line. */}
      {steps.map((step, i) => (
        <div
          key={`dot-${i}`}
          className={`sa-banner__dot sa-banner__dot--${i + 1}${
            step.active ? ' sa-banner__dot--active' : ''
          }`}
        >
          <span className="sa-banner__dot-glow" />
          {step.active && <span className="sa-banner__dot-ring" />}
          <span className="sa-banner__dot-core" />
        </div>
      ))}

      {/* Date + time cards — two-line content (date on top, HH:mm bold below). */}
      {steps.map((step, i) => (
        <div
          key={`card-${i}`}
          className={`sa-banner__date sa-banner__date--${i + 1}`}
        >
          {loading ? (
            <>
              <span className="sa-banner__date-skeleton sa-banner__date-skeleton--month" aria-hidden />
              <span className="sa-banner__date-skeleton sa-banner__date-skeleton--day" aria-hidden />
            </>
          ) : (
            <>
              <span className="sa-banner__date-month">{step.date}</span>
              <span className="sa-banner__date-day">{padTime(step.time)}</span>
            </>
          )}
        </div>
      ))}
    </div>
  </section>
);

export default AnnouncementBanner;
